import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Facebook,
  Github,
  Mail,
  Twitter,
  Loader2,
  Sparkles,
  ShieldCheck,
  Zap,
} from "lucide-react";

import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

import { loginUser } from "slices/thunk";
import { resetLogin } from "slices/auth/login/reducer";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "Common/withRouter";
import { createSelector } from "reselect";

import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

const featureHighlights = [
  {
    icon: Sparkles,
    title: "AI-assisted workflows",
    description: "Automate campaigns and conversations with intelligent agents.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-ready",
    description: "Granular permissions and audited access for your whole team.",
  },
  {
    icon: Zap,
    title: "Real-time everywhere",
    description: "Live data, presence, and notifications across every surface.",
  },
];

const Login = (props: any) => {
  document.title = "Login | EngageHub IQ";

  const dispatch = useDispatch<any>();
  const selectLogin = createSelector(
    (state: any) => state.Register,
    (state: any) => state.Login,
    (register, login) => ({
      user: register.user,
      success: login.success,
      error: login.error,
      loading: login.loading,
    })
  );

  const { user, success, error, loading } = useSelector(selectLogin);

  useEffect(() => {
    dispatch(resetLogin());
  }, [dispatch]);

  const credential = localStorage.getItem("credential");
  const parsedCredential = credential
    ? JSON.parse(credential)
    : { email: "", password: "" };

  const validation: any = useFormic({
    enableReinitialize: true,
    initialValues: {
      email: user.email || parsedCredential.email || "",
      password: parsedCredential.password || "",
      remember: !!credential,
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values: any) => {
      if (values.remember) {
        localStorage.setItem(
          "credential",
          JSON.stringify({ email: values.email, password: values.password })
        );
      } else {
        localStorage.removeItem("credential");
      }
      dispatch(loginUser(values, props.router.navigate));
    },
  });

  return (
    <div className="relative grid min-h-screen grid-cols-1 overflow-hidden bg-background lg:grid-cols-2">
      {/* Left: hero / brand panel */}
      <div className="relative hidden lg:block">
        {/* Animated gradient mesh */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-primary via-accent to-primary bg-[length:200%_200%] animate-gradient"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_40%),radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.12),transparent_45%)]"
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14 text-primary-foreground">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-lg font-semibold tracking-tight"
          >
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-white/15 backdrop-blur">
              <Sparkles className="h-5 w-5" />
            </span>
            <span>EngageHub IQ</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            <h1 className="max-w-md text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
              The intelligent engagement platform for modern teams.
            </h1>
            <p className="max-w-md text-base text-white/80">
              Orchestrate conversations, campaigns, and workflows in one
              unified, AI-native workspace.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="grid gap-4 sm:grid-cols-3"
          >
            {featureHighlights.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur-md"
              >
                <Icon className="mb-2 h-5 w-5" />
                <p className="text-sm font-semibold">{title}</p>
                <p className="mt-1 text-xs text-white/70">{description}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="relative flex items-center justify-center px-4 py-10 sm:px-6 lg:px-12">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 lg:hidden"
        >
          <div className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="mb-8 text-center lg:text-left">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
              Welcome back
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              Sign in to your workspace
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your credentials to access the EngageHub IQ platform.
            </p>
          </div>

          <form
            id="signInForm"
            onSubmit={(event) => {
              event.preventDefault();
              validation.handleSubmit();
              return false;
            }}
            className="space-y-5"
          >
            {success && (
              <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                You have <b>successfully</b> signed in.
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error === "LockedOut" ? (
                  <span>
                    You have been <b>locked out</b> due to multiple failed
                    attempts.
                  </span>
                ) : (
                  <span>
                    You have <b>failed</b> signed in.
                  </span>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Username / Email</Label>
              <Input
                id="email"
                name="email"
                type="text"
                placeholder="Enter your email or username"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}
                aria-invalid={
                  validation.touched.email && !!validation.errors.email
                }
              />
              {validation.touched.email && validation.errors.email ? (
                <p
                  id="email-error"
                  className="text-xs font-medium text-destructive"
                >
                  {validation.errors.email}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.password || ""}
                aria-invalid={
                  validation.touched.password && !!validation.errors.password
                }
              />
              {validation.touched.password && validation.errors.password ? (
                <p
                  id="password-error"
                  className="text-xs font-medium text-destructive"
                >
                  {validation.errors.password}
                </p>
              ) : null}
            </div>

            <label
              htmlFor="remember"
              className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
            >
              <input
                id="remember"
                name="remember"
                type="checkbox"
                checked={validation.values.remember || false}
                onChange={validation.handleChange}
                className={cn(
                  "h-4 w-4 rounded border border-border bg-background",
                  "text-primary focus:ring-2 focus:ring-ring focus:ring-offset-1"
                )}
              />
              Remember me on this device
            </label>

            <Button
              type="submit"
              size="lg"
              variant="premium"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <span>Sign in</span>
              )}
            </Button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <Button
                variant="outline"
                size="icon"
                type="button"
                aria-label="Sign in with Facebook"
                onClick={(e) => e.preventDefault()}
              >
                <Facebook className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                type="button"
                aria-label="Sign in with Google"
                onClick={(e) => e.preventDefault()}
              >
                <Mail className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                type="button"
                aria-label="Sign in with Twitter"
              >
                <Twitter className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                type="button"
                aria-label="Sign in with GitHub"
              >
                <Github className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="font-semibold text-primary hover:underline"
              >
                Create one
              </Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default withRouter(Login);
